import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../../lib/api';
import { cn } from '../../lib/utils';
import {
    Trophy, TrendingUp, TrendingDown, Minus, ArrowRight,
    Crown, Shield, Star, Medal, ChevronUp
} from 'lucide-react';

const LEAGUES = {
    platinum: { label: 'Platine', color: 'from-cyan-300 to-blue-400', text: 'text-cyan-300', bg: 'bg-cyan-500/10', icon: Crown, min: 1500 },
    gold: { label: 'Or', color: 'from-amber-300 to-yellow-500', text: 'text-amber-400', bg: 'bg-amber-500/10', icon: Trophy, min: 1200 },
    silver: { label: 'Argent', color: 'from-slate-300 to-slate-400', text: 'text-slate-300', bg: 'bg-slate-400/10', icon: Shield, min: 900 },
    bronze: { label: 'Bronze', color: 'from-orange-400 to-amber-600', text: 'text-orange-400', bg: 'bg-orange-500/10', icon: Medal, min: 0 },
};

const TREND_CONFIG = {
    up: { icon: TrendingUp, label: 'En hausse', color: 'text-green-400' },
    down: { icon: TrendingDown, label: 'En baisse', color: 'text-red-400' },
    stable: { icon: Minus, label: 'Stable', color: 'text-muted-foreground' },
};

export default function HomlyScoreWidget({ basePath }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            const res = await api.get('/leaderboard/my-rank');
            setData(res.data);
        } catch { /* ignore */ }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    if (loading) {
        return (
            <div className="bg-card rounded-2xl border border-border p-5 animate-pulse">
                <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-muted" />
                    <div className="flex-1 space-y-2">
                        <div className="h-5 bg-muted rounded w-40" />
                        <div className="h-4 bg-muted rounded w-24" />
                    </div>
                </div>
            </div>
        );
    }

    if (!data || !data.stats) return null;

    const { stats, global_rank, global_total, global_percentile } = data;
    const league = LEAGUES[stats.league] || LEAGUES.bronze;
    const trend = TREND_CONFIG[stats.trend] || TREND_CONFIG.stable;
    const LeagueIcon = league.icon;
    const TrendIcon = trend.icon;

    // Calculate progress to next league
    const currentLeagueMin = league.min;
    const leagueOrder = ['bronze', 'silver', 'gold', 'platinum'];
    const currentIdx = leagueOrder.indexOf(stats.league);
    const nextLeague = currentIdx < leagueOrder.length - 1 ? LEAGUES[leagueOrder[currentIdx + 1]] : null;
    const progressPercent = nextLeague
        ? Math.min(100, Math.round(((stats.elo - currentLeagueMin) / (nextLeague.min - currentLeagueMin)) * 100))
        : 100;

    return (
        <div className="bg-card backdrop-blur-sm rounded-2xl border border-border overflow-hidden" data-testid="homlyscore-widget">
            {/* Gradient accent bar */}
            <div className={cn("h-1 bg-gradient-to-r", league.color)} />

            <div className="p-5">
                <div className="flex items-start gap-4">
                    {/* League badge */}
                    <motion.div
                        className={cn("w-16 h-16 rounded-2xl flex items-center justify-center bg-gradient-to-br shrink-0", league.color)}
                        initial={{ scale: 0.8 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring', stiffness: 200 }}
                    >
                        <LeagueIcon className="w-8 h-8 text-white drop-shadow-lg" />
                    </motion.div>

                    {/* Score + Rank info */}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                            <h3 className="font-bold text-lg">HomlyScore</h3>
                            <TrendIcon className={cn("w-4 h-4", trend.color)} />
                        </div>
                        <div className="flex items-baseline gap-3">
                            <motion.span
                                className={cn("text-3xl font-black", league.text)}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 }}
                            >
                                {stats.elo}
                            </motion.span>
                            <span className="text-sm text-muted-foreground">
                                pts · Ligue {league.label}
                            </span>
                        </div>
                    </div>

                    {/* Rank badge */}
                    <Link
                        to={`${basePath}/leaderboard`}
                        className="flex flex-col items-center gap-0.5 p-2.5 rounded-xl bg-white/5 hover:bg-white/10 transition-colors shrink-0"
                        data-testid="homlyscore-rank-link"
                    >
                        <span className="text-2xl font-black text-foreground">#{global_rank}</span>
                        <span className="text-[10px] text-muted-foreground font-medium">/{global_total} national</span>
                    </Link>
                </div>

                {/* Progress bar to next league */}
                {nextLeague && (
                    <div className="mt-4">
                        <div className="flex items-center justify-between mb-1.5">
                            <span className="text-xs text-muted-foreground">
                                Progression vers {nextLeague.label}
                            </span>
                            <span className="text-xs font-bold" style={{ color: nextLeague.min > stats.elo ? undefined : 'inherit' }}>
                                {nextLeague.min - stats.elo > 0
                                    ? <span className="text-muted-foreground">{nextLeague.min - stats.elo} pts restants</span>
                                    : <span className="text-green-400">Promu !</span>
                                }
                            </span>
                        </div>
                        <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                            <motion.div
                                className={cn("h-full rounded-full bg-gradient-to-r", nextLeague.color)}
                                initial={{ width: 0 }}
                                animate={{ width: `${progressPercent}%` }}
                                transition={{ duration: 1, ease: 'easeOut', delay: 0.3 }}
                            />
                        </div>
                    </div>
                )}

                {/* Stats row */}
                <div className="mt-4 grid grid-cols-3 gap-3">
                    <div className="text-center p-2.5 rounded-xl bg-white/[0.03]">
                        <p className="text-lg font-bold text-foreground">{stats.avg_rating?.toFixed(1) || '—'}</p>
                        <p className="text-[10px] text-muted-foreground">Note moy.</p>
                    </div>
                    <div className="text-center p-2.5 rounded-xl bg-white/[0.03]">
                        <p className="text-lg font-bold text-foreground">{stats.total_ratings || 0}</p>
                        <p className="text-[10px] text-muted-foreground">Avis</p>
                    </div>
                    <div className="text-center p-2.5 rounded-xl bg-white/[0.03]">
                        <p className="text-lg font-bold text-foreground flex items-center justify-center gap-1">
                            Top {global_percentile || 0}%
                        </p>
                        <p className="text-[10px] text-muted-foreground">National</p>
                    </div>
                </div>

                {/* Badges preview */}
                {stats.badges && stats.badges.length > 0 && (
                    <div className="mt-3 flex items-center gap-1.5 flex-wrap">
                        {stats.badges.slice(0, 4).map((b, i) => {
                            const label = typeof b === 'string' ? b.replace('_', ' ') : (b.name || b.id || '');
                            const key = typeof b === 'string' ? b : (b.id || i);
                            return (
                                <span key={key} className={cn("text-[10px] px-2 py-0.5 rounded-full border font-semibold", league.bg, league.text, "border-current/20")}>
                                    {label}
                                </span>
                            );
                        })}
                        {stats.badges.length > 4 && (
                            <span className="text-[10px] text-muted-foreground">+{stats.badges.length - 4}</span>
                        )}
                    </div>
                )}

                {/* CTA */}
                <Link
                    to={`${basePath}/leaderboard`}
                    className="mt-4 flex items-center justify-center gap-2 w-full p-2.5 rounded-xl bg-white/5 hover:bg-white/10 transition-colors text-sm font-semibold text-muted-foreground hover:text-foreground"
                    data-testid="homlyscore-leaderboard-link"
                >
                    <Trophy className="w-4 h-4" />
                    Voir le classement national
                    <ArrowRight className="w-3.5 h-3.5" />
                </Link>
            </div>
        </div>
    );
}
